import { c as _c } from "react/compiler-runtime";
import { MapPin, User, LucideIcon, UserPlus, Plus, Shield, Building2, Search, Stethoscope, FileCode } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { useCPTSearch } from "@/src/hooks/use-cpt-search";
import { useDebounce } from "@/src/hooks/use-debounce";
import { useGeocodingSearch } from "@/src/hooks/use-geocoding-search";
import { useICD10Search } from "@/src/hooks/use-icd10-search";
import { useNPISearch } from "@/src/hooks/use-npi-search";
import { searchCoverages } from "@/src/lib/coverage-utils";
import { usePatientSearch } from "@/src/lib/data-fetchers/patients-fetchers";
import { searchLocationsByText } from "@/src/lib/location-search";
import { patientsSearch } from "@/src/lib/patient";
import { type PayerWeighting, searchPayer } from "@/src/lib/payer-search";
import { providersSearch } from "@/src/lib/provider-search";
import { Coverage, getCoverageDisplayName } from "@/src/models/coverage";
import { CPT, getCPTDisplayName } from "@/src/models/cpt";
import { ICD10, getICD10DisplayName } from "@/src/models/icd10";
import { Location, getLocationDisplayName } from "@/src/models/location";
import { convertNPIResultToProvider } from "@/src/models/npi-registry";
import { Patient, getPatientDisplayName, getPatientPotentialMatchDisplayName } from "@/src/models/patient";
import { Payer, getPayerDisplayName } from "@/src/models/payer";
import { Practitioner, getProviderDisplayName } from "@/src/models/practitioner";

// Define the allowed entity types for EntityMatcher
export type EntityMatcherType = Patient | Practitioner | Location | Coverage | Payer | ICD10 | CPT;
export type EntityType = "patient" | "practitioner" | "location" | "coverage" | "payer" | "icd10" | "cpt";
interface ExternalDataSource<T extends EntityMatcherType> {
  search: (query: string) => void;
  isLoading: boolean;
  error: string | null;
  results: T[];
  shouldShow: (query: string) => boolean;
  getDisplayName: (item: T) => string;
  getIcon: (item: T) => LucideIcon;
  groupTitle: string;
}
interface EntityMatcherConfig<T extends EntityMatcherType> {
  displayConfig: {
    getDisplayName: (entity: T) => string;
    getPotentialMatchDisplayName?: (entity: T) => string;
    entityIcon: LucideIcon;
    createNewIcon: LucideIcon;
    entityListTitle: string;
    createNewTitle: string;
    placeholder: string;
    renderBadge?: (entity: T) => React.ReactNode;
  };
  externalDataSources: ExternalDataSource<T>[];
  search: (entities: T[], query: string, weighting?: PayerWeighting) => T[];
  generateInitialSearchQuery?: (entity: T) => string;
}

// Centralized configuration object for static config
const ENTITY_MATCHER_STATIC_CONFIGS = {
  patient: {
    displayConfig: {
      getDisplayName: getPatientDisplayName,
      getPotentialMatchDisplayName: getPatientPotentialMatchDisplayName,
      entityIcon: User,
      createNewIcon: UserPlus,
      entityListTitle: "Patients",
      createNewTitle: "Create new patient",
      placeholder: "Search patients..."
    },
    search: (patients: Patient[], query: string) => patientsSearch(patients, query)
  },
  practitioner: {
    displayConfig: {
      getDisplayName: getProviderDisplayName,
      entityIcon: User,
      createNewIcon: UserPlus,
      entityListTitle: "Providers",
      createNewTitle: "Create new provider",
      placeholder: "Search providers or NPI registry..."
    },
    search: (practitioners: Practitioner[], query: string) => providersSearch(practitioners, query),
    generateInitialSearchQuery: (practitioner: Practitioner) => {
      if (practitioner.npiNumber) {
        return practitioner.npiNumber;
      }
      const name = [practitioner.givenName, practitioner.familyName].filter(Boolean).join(" ");
      return name || "";
    }
  },
  location: {
    displayConfig: {
      getDisplayName: getLocationDisplayName,
      entityIcon: MapPin,
      createNewIcon: Plus,
      entityListTitle: "Locations",
      createNewTitle: "Create new location",
      placeholder: "Search locations or addresses..."
    },
    search: (locations: Location[], query: string) => searchLocationsByText(locations, query),
    generateInitialSearchQuery: (location: Location) => {
      const parts = [location.address?.line1, location.address?.city, location.address?.state, location.address?.postalCode].filter(Boolean);
      return parts.join(", ");
    }
  },
  coverage: {
    displayConfig: {
      getDisplayName: getCoverageDisplayName,
      entityIcon: Shield,
      createNewIcon: Plus,
      entityListTitle: "Coverages",
      createNewTitle: "Create new coverage",
      placeholder: "Search insurance coverage...",
      renderBadge: (coverage: Coverage) => {
        if (coverage.order === 1) {
          return <Badge variant="secondary" className="text-xs rounded-full bg-background text-foreground border-foreground">
              Primary
            </Badge>;
        }
        return null;
      }
    },
    search: (coverages: Coverage[], query: string) => searchCoverages(coverages, query)
  },
  payer: {
    displayConfig: {
      getDisplayName: getPayerDisplayName,
      entityIcon: Building2,
      createNewIcon: Search,
      entityListTitle: "Payers",
      createNewTitle: "Search for payor",
      placeholder: "Search for payor..."
    },
    search: (payers: Payer[], query: string, weighting?: PayerWeighting) => searchPayer(payers, query, weighting)
  },
  icd10: {
    displayConfig: {
      getDisplayName: getICD10DisplayName,
      entityIcon: Stethoscope,
      createNewIcon: Search,
      entityListTitle: "ICD-10 Codes",
      createNewTitle: "Search ICD-10 codes",
      placeholder: "Search ICD-10 codes..."
    },
    search: () => {
      // No-op: ICD-10 search is handled server-side
      return [];
    }
  },
  cpt: {
    displayConfig: {
      getDisplayName: getCPTDisplayName,
      entityIcon: FileCode,
      createNewIcon: Search,
      entityListTitle: "CPT Codes",
      createNewTitle: "Search CPT codes",
      placeholder: "Search CPT codes..."
    },
    search: () => {
      return [];
    }
  }
} as const;

// Hook to get complete entity matcher configuration including external data sources
export const useEntityMatcherConfig = type => {
  const $ = _c(12);
  const npiSource = useNPISource();
  const geocodingSource = useGeocodingSource();
  const icd10Source = useICD10Source();
  const cptSource = useCPTSource();
  const patientSource = usePatientSearchSource();
  let t0;
  let config;
  bb0: switch (type) {
    case "practitioner":
      {
        let t1;
        if ($[0] !== npiSource) {
          t1 = {
            ...ENTITY_MATCHER_STATIC_CONFIGS.practitioner,
            externalDataSources: [npiSource]
          };
          $[0] = npiSource;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        config = t1;
        break bb0;
      }
    case "location":
      {
        let t1;
        if ($[2] !== geocodingSource) {
          t1 = {
            ...ENTITY_MATCHER_STATIC_CONFIGS.location,
            externalDataSources: [geocodingSource]
          };
          $[2] = geocodingSource;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        config = t1;
        break bb0;
      }
    case "patient":
      {
        let t1;
        if ($[4] !== patientSource) {
          t1 = {
            ...ENTITY_MATCHER_STATIC_CONFIGS.patient,
            externalDataSources: [patientSource]
          };
          $[4] = patientSource;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        config = t1;
        break bb0;
      }
    case "coverage":
      {
        let t1;
        if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = {
            ...ENTITY_MATCHER_STATIC_CONFIGS.coverage,
            externalDataSources: []
          };
          $[6] = t1;
        } else {
          t1 = $[6];
        }
        config = t1;
        break bb0;
      }
    case "payer":
      {
        let t1;
        if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = {
            ...ENTITY_MATCHER_STATIC_CONFIGS.payer,
            externalDataSources: []
          };
          $[7] = t1;
        } else {
          t1 = $[7];
        }
        config = t1;
        break bb0;
      }
    case "icd10":
      {
        let t1;
        if ($[8] !== icd10Source) {
          t1 = {
            ...ENTITY_MATCHER_STATIC_CONFIGS.icd10,
            externalDataSources: [icd10Source]
          };
          $[8] = icd10Source;
          $[9] = t1;
        } else {
          t1 = $[9];
        }
        config = t1;
        break bb0;
      }
    case "cpt":
      {
        let t1;
        if ($[10] !== cptSource) {
          t1 = {
            ...ENTITY_MATCHER_STATIC_CONFIGS.cpt,
            externalDataSources: [cptSource]
          };
          $[10] = cptSource;
          $[11] = t1;
        } else {
          t1 = $[11];
        }
        config = t1;
        break bb0;
      }
    default:
      {
        throw new Error(`Unknown entity type: ${type}`);
      }
  }
  t0 = config as EntityMatcherConfig<EntityMatcherType>;
  return t0;
};
export const useGeocodingSource = () => {
  const $ = _c(5);
  const [currentSearch, setCurrentSearch] = useState("");
  const {
    results: geocodeResults,
    loading: geocodeLoading,
    error: geocodeError
  } = useGeocodingSearch(currentSearch);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = query => {
      setCurrentSearch(query);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const search = t0;
  const shouldShow = _temp;
  let t1;
  if ($[1] !== geocodeError || $[2] !== geocodeLoading || $[3] !== geocodeResults) {
    t1 = {
      search,
      isLoading: geocodeLoading,
      error: geocodeError,
      results: geocodeResults,
      shouldShow,
      getDisplayName: getLocationDisplayName,
      getIcon: _temp2,
      groupTitle: "External addresses"
    };
    $[1] = geocodeError;
    $[2] = geocodeLoading;
    $[3] = geocodeResults;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const useNPISource = () => {
  const $ = _c(7);
  const [currentSearch, setCurrentSearch] = useState("");
  const {
    results: npiResults,
    loading: npiLoading,
    error: npiError
  } = useNPISearch(currentSearch);
  let t0;
  if ($[0] !== npiResults) {
    t0 = npiResults.map(convertNPIResultToProvider);
    $[0] = npiResults;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const results = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = query => {
      setCurrentSearch(query);
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const search = t1;
  const shouldShow = _temp3;
  let t2;
  if ($[3] !== npiError || $[4] !== npiLoading || $[5] !== results) {
    t2 = {
      search,
      isLoading: npiLoading,
      error: npiError,
      results,
      shouldShow,
      getDisplayName: getProviderDisplayName,
      getIcon: _temp4,
      groupTitle: "NPI Registry Providers"
    };
    $[3] = npiError;
    $[4] = npiLoading;
    $[5] = results;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};

/**
 * External data source that resolves patient matches via the server-side
 * `/api/patients/search` endpoint. Lets the EntityMatcher search the full set
 * of patients without ever loading them into the client up-front.
 */
const usePatientSearchSource = () => {
  const $ = _c(7);
  const [currentSearch, setCurrentSearch] = useState("");
  const debouncedSearch = useDebounce(currentSearch, 200);
  const t0 = debouncedSearch.length >= 2;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      enabled: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    patients,
    isFetching,
    isLoading,
    error
  } = usePatientSearch(debouncedSearch, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = query => {
      setCurrentSearch(query);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const search = t2;
  const shouldShow = _temp5;
  const errorMessage = error instanceof Error ? error.message : null;
  const t3 = isLoading || isFetching;
  let t4;
  if ($[3] !== errorMessage || $[4] !== patients || $[5] !== t3) {
    t4 = {
      search,
      isLoading: t3,
      error: errorMessage,
      results: patients,
      shouldShow,
      getDisplayName: getPatientDisplayName,
      getIcon: _temp6,
      groupTitle: "Patients"
    };
    $[3] = errorMessage;
    $[4] = patients;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
const useICD10Source = () => {
  const $ = _c(5);
  const [currentSearch, setCurrentSearch] = useState("");
  const {
    results: icd10Results,
    loading: icd10Loading,
    error: icd10Error
  } = useICD10Search(currentSearch);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = query => {
      setCurrentSearch(query);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const search = t0;
  const shouldShow = _temp7;
  let t1;
  if ($[1] !== icd10Error || $[2] !== icd10Loading || $[3] !== icd10Results) {
    t1 = {
      search,
      isLoading: icd10Loading,
      error: icd10Error,
      results: icd10Results,
      shouldShow,
      getDisplayName: getICD10DisplayName,
      getIcon: _temp8,
      groupTitle: "ICD-10 Codes"
    };
    $[1] = icd10Error;
    $[2] = icd10Loading;
    $[3] = icd10Results;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const useCPTSource = () => {
  const $ = _c(5);
  const [currentSearch, setCurrentSearch] = useState("");
  const {
    results: cptResults,
    loading: cptLoading,
    error: cptError
  } = useCPTSearch(currentSearch);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = query => {
      setCurrentSearch(query);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const search = t0;
  const shouldShow = _temp9;
  let t1;
  if ($[1] !== cptError || $[2] !== cptLoading || $[3] !== cptResults) {
    t1 = {
      search,
      isLoading: cptLoading,
      error: cptError,
      results: cptResults,
      shouldShow,
      getDisplayName: getCPTDisplayName,
      getIcon: _temp10,
      groupTitle: "CPT Codes"
    };
    $[1] = cptError;
    $[2] = cptLoading;
    $[3] = cptResults;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
function _temp(query_0) {
  return query_0.length >= 3;
}
function _temp2(_item) {
  return MapPin;
}
function _temp3(query_0) {
  return query_0.length >= 3;
}
function _temp4(_item) {
  return User;
}
function _temp5(query_0) {
  return query_0.trim().length >= 2;
}
function _temp6(_item) {
  return User;
}
function _temp7(query_0) {
  return query_0.trim().length >= 2;
}
function _temp8(_icd10) {
  return Stethoscope;
}
function _temp9(query_0) {
  return query_0.trim().length >= 2;
}
function _temp10(_cpt) {
  return FileCode;
}